// src/pages/SubmittedFormsListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './SubmittedFormsListPage.css';

import { baseUrl } from '../utils/config';

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    console.log("Retrieved token:", token ? "Token present" : "No token found in localStorage");
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

function SubmittedFormsListPage() {
    const [formTemplates, setFormTemplates] = useState([]);
    const [selectedFormTemplateId, setSelectedFormTemplateId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [submittedForms, setSubmittedForms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const fetchFormTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${baseUrl}/api/form-names`, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to fetch form templates (Status: ${response.status})`);
            }
            const data = await response.json();
            setFormTemplates(data);
        } catch (err) {
            setError(err.message);
            console.error("Error fetching form templates:", err);
            if (err instanceof SyntaxError && err.message.includes('Unexpected token')) {
                setError("Failed to parse server response. Did the backend send HTML instead of JSON? (Check /api/form-names)");
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchSubmittedForms = useCallback(async (filters = {}) => {
        setLoading(true);
        setError(null);

        const queryParams = new URLSearchParams();

        if (filters.formTemplateId && filters.formTemplateId !== 'all') {
            queryParams.append('formTemplateId', filters.formTemplateId);
        }
        if (filters.startDate) {
            queryParams.append('startDate', filters.startDate);
        }
        if (filters.endDate) {
            queryParams.append('endDate', filters.endDate);
        }

        let url = `${baseUrl}/api/submitted-savedforms`;
        if (queryParams.toString()) {
            url = `${url}?${queryParams.toString()}`;
        }

        try {
            const response = await fetch(url, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to fetch submitted forms (Status: ${response.status})`);
            }
            const data = await response.json();
            setSubmittedForms(data);
        } catch (err) {
            setError(err.message);
            console.error("Error fetching submitted forms:", err);
            if (err instanceof SyntaxError && err.message.includes('Unexpected token')) {
                setError("Failed to parse server response for submitted forms. Did the backend send HTML instead of JSON? (Check /api/submitted-savedforms)");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFormTemplates();

        // Parse URL parameters on initial load
        const params = new URLSearchParams(location.search);
        const formTemplateIdParam = params.get('formTemplateId') || '';
        const startDateParam = params.get('startDate') || '';
        const endDateParam = params.get('endDate') || '';

        setSelectedFormTemplateId(formTemplateIdParam);
        setStartDate(startDateParam);
        setEndDate(endDateParam);

        // Fetch forms based on URL params if they exist
        // Added a check to only fetch if params are present or if table should be loaded initially
        // Otherwise, the table will be empty until a search is performed.
        if (formTemplateIdParam || startDateParam || endDateParam) {
            fetchSubmittedForms({
                formTemplateId: formTemplateIdParam,
                startDate: startDateParam,
                endDate: endDateParam
            });
        }
        // If you want to load all forms by default when no filters are in URL, uncomment the else block
        // else {
        //     fetchSubmittedForms({});
        // }
    }, [location.search, fetchSubmittedForms]);


    const handleSearchForms = () => {
        const filters = {};
        if (selectedFormTemplateId) {
            filters.formTemplateId = selectedFormTemplateId;
        }
        if (startDate) {
            filters.startDate = startDate;
        }
        if (endDate) {
            filters.endDate = endDate;
        }

        const newSearchParams = new URLSearchParams();
        if (filters.formTemplateId) newSearchParams.append('formTemplateId', filters.formTemplateId);
        if (filters.startDate) newSearchParams.append('startDate', filters.startDate);
        if (filters.endDate) newSearchParams.append('endDate', filters.endDate);

        navigate(`?${newSearchParams.toString()}`, { replace: true });
        fetchSubmittedForms(filters);
    };

    const handleClearFilters = () => {
        setSelectedFormTemplateId('');
        setStartDate('');
        setEndDate('');
        setSubmittedForms([]);
        setError(null);

        navigate(location.pathname, { replace: true });
    };

    const handleViewForm = (formId) => {
        console.log('Navigating to form with ID:', formId);
        if (!formId) {
            console.error('ERROR: Attempted to navigate with undefined or null formId!');
            return;
        }
        navigate(`/dashboard/submitted-forms/view/${formId}`);
    };

    return (
        <div className="submitted-forms-container">
            <h2 className="submitted-forms-heading">Search Submitted Forms</h2>

            <div className="filter-card">
                <div className="filter-row">
                    <label htmlFor="formSelect" className="filter-label">Select Form:</label>
                    <select
                        id="formSelect"
                        value={selectedFormTemplateId}
                        onChange={(e) => setSelectedFormTemplateId(e.target.value)}
                        className="filter-select"
                    >
                        <option value="">-- Select a Form --</option>
                        <option value="all">All Forms</option>
                        {formTemplates.map((template) => (
                            <option key={template._id} value={template._id}>
                                {template.formName}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-row">
                    <label htmlFor="startDate" className="filter-label">From Date:</label>
                    <input
                        type="date"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="filter-date-input"
                    />
                </div>

                <div className="filter-row">
                    <label htmlFor="endDate" className="filter-label">To Date:</label>
                    <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="filter-date-input"
                    />
                </div>

                <div className="button-group">
                    <button onClick={handleSearchForms} disabled={loading} className="primary-button">
                        {loading ? 'Searching...' : 'Search Forms'}
                    </button>
                    <button onClick={handleClearFilters} className="secondary-button">
                        Clear Filters
                    </button>
                </div>
                {error && <p className="error-text">Error: {error}</p>}
            </div>

            <h3 className="submitted-forms-subheading">Submitted Forms List:</h3>
            {loading && <p>Loading submitted forms...</p>}
            {!loading && submittedForms.length === 0 && !error && (
                <p>No forms found for the selected criteria. Try adjusting your filters.</p>
            )}
            {!loading && submittedForms.length > 0 && (
                <table className="submitted-forms-table">
                    <thead>
                        <tr>
                            <th>Form Number</th>
                            <th>Form Name</th>
                            <th>Submitted By</th>
                            <th>Status</th>
                            <th>Submission Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {submittedForms.map((form) => (
                            <tr key={form._id}>
                                {/* ADDED data-label ATTRIBUTES HERE */}
                                <td data-label="Form Number">{form.formNumber || 'N/A'}</td>
                                <td data-label="Form Name">{form.formTemplateId ? form.formTemplateId.formName : 'N/A'}</td>
                                <td data-label="Submitted By">{form.submittedBy ? form.submittedBy.name : 'N/A'}</td>
                                <td data-label="Status">{form.status}</td>
                                <td data-label="Submission Date">
                                    {new Date(form.createdAt).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                    })}
                                </td>
                                <td data-label="Actions">
                                    <button onClick={() => handleViewForm(form._id)} className="view-button">
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default SubmittedFormsListPage;